import { StatusPill } from "@playbookai/ui";
import type { KpiCurrentState, KpiLifecycleStatus } from "../api/types.js";
import {
  LIFECYCLE_LABEL,
  LIFECYCLE_TONE,
  MEASUREMENT_HINT,
  MEASUREMENT_LABEL,
  MEASUREMENT_TONE,
  PERFORMANCE_LABEL,
  PERFORMANCE_TONE,
} from "./kpi-presentation.js";

/** Lifecycle-only pill. */
export function KpiLifecyclePill({
  status,
}: {
  status: KpiLifecycleStatus;
}): JSX.Element {
  return (
    <StatusPill tone={LIFECYCLE_TONE[status]}>
      {LIFECYCLE_LABEL[status]}
    </StatusPill>
  );
}

/**
 * The two MEASURED status dimensions: measurement + performance. A KPI with no
 * projection row (defensive) still reads the `not_connected` / `not_available`
 * default. The measurement hint explains WHY a KPI isn't measurable (the
 * deferred-data-plane honest signal).
 *
 * LIFECYCLE IS DELIBERATELY ABSENT. It is the KPI's headline and belongs to the
 * page header; repeating the identical pill further down reads as a second fact
 * rather than the same one twice — the duplicate-status problem this component
 * used to be half of. The three dimensions stay independent (KPI Ecosystem §6);
 * only where they RENDER changed.
 */
export function KpiStatusBadges({
  state,
  prefixed = false,
  hint = true,
  performanceFirst = false,
}: {
  /** Performance before measurement, as the canvas orders the Performance
   * card's pills; KPI details keeps measurement first. */
  performanceFirst?: boolean;
  state: KpiCurrentState | null;
  /** Name the dimension in each pill ("Measurement · Current"), as the
   * canvas does in KPI details (d37). */
  prefixed?: boolean;
  /** The one-line explanation under the pills; off where the pills sit in a
   * row with other readouts. */
  hint?: boolean;
}): JSX.Element {
  const measurement = state?.measurementStatus ?? "not_connected";
  const performance = state?.performanceStatus ?? "not_available";
  return (
    <div className="space-y-2">
      {/* Rendered in the intended order — never reversed with CSS — so
          assistive technology reads the pills as the screen shows them. */}
      <div className="flex flex-wrap items-center gap-2">
        {(performanceFirst
          ? (["performance", "measurement"] as const)
          : (["measurement", "performance"] as const)
        ).map((dimension) =>
          dimension === "measurement" ? (
            <StatusPill key={dimension} tone={MEASUREMENT_TONE[measurement]}>
              {prefixed ? "Measurement · " : ""}
              {MEASUREMENT_LABEL[measurement]}
            </StatusPill>
          ) : (
            <StatusPill key={dimension} tone={PERFORMANCE_TONE[performance]}>
              {prefixed ? "Performance · " : ""}
              {PERFORMANCE_LABEL[performance]}
            </StatusPill>
          ),
        )}
      </div>
      {hint ? (
        <p className="text-dense-1 text-pb-text-muted">
          {MEASUREMENT_HINT[measurement]}
        </p>
      ) : null}
    </div>
  );
}
